/**
 * The completion markers + their streaming filter — a leaf module (no internal
 * imports) so both the render path and the completion engine can use it
 * without an import cycle.
 *
 * The model is instructed (system-prompt.ts AGENT_PROTOCOL) to end its FINAL
 * answer with {@link DONE_MARKER}, and may add {@link NOVERIFY_MARKER} when the
 * change is small enough to skip the project checks. The filter strips both from
 * everything the user sees while recording which appeared — the signals the
 * completion policy (finish.ts) keys on.
 */

/**
 * Completion marker the model must end its FINAL answer with (ASCII-only — see
 * the PS5.1 UTF-8 round-trip caveat). Stripped from display by the filter.
 */
export const DONE_MARKER = '[[FX:DONE]]';

/**
 * Optional marker the model adds (alongside {@link DONE_MARKER}) to declare the
 * change trivial — small enough that no typecheck/lint/test could be affected.
 * The completion policy skips automatic verification only when the change also
 * clears a blast-radius guard (see change-significance.ts).
 */
export const NOVERIFY_MARKER = '[[FX:NOVERIFY]]';

const MARKERS: readonly string[] = [DONE_MARKER, NOVERIFY_MARKER];

/**
 * Strips the markers out of a token stream without ever flushing a partial
 * marker (a marker can arrive split across chunks). `push` returns the
 * displayable text for each delta; call `flush` once at stream end.
 */
export interface DoneFilter {
  push(delta: string): string;
  flush(): string;
  /** {@link DONE_MARKER} appeared in the stream. */
  readonly sawMarker: boolean;
  /** {@link NOVERIFY_MARKER} appeared in the stream. */
  readonly sawNoVerify: boolean;
}

/** Longest suffix of `s` that is a (proper) prefix of ANY marker. */
function markerPrefixSuffix(s: string): number {
  let best = 0;
  for (const marker of MARKERS) {
    const max = Math.min(s.length, marker.length - 1);
    for (let len = max; len > best; len--) {
      if (s.endsWith(marker.slice(0, len))) {
        best = len;
        break;
      }
    }
  }
  return best;
}

export function createDoneFilter(): DoneFilter {
  let tail = '';
  let sawDone = false;
  let sawNoVerify = false;

  /** Remove every complete marker occurrence, recording which were seen. */
  const strip = (input: string): string => {
    let buf = input;
    for (;;) {
      let at = -1;
      let hit: string | null = null;
      for (const marker of MARKERS) {
        const idx = buf.indexOf(marker);
        if (idx !== -1 && (at === -1 || idx < at)) {
          at = idx;
          hit = marker;
        }
      }
      if (at === -1 || hit === null) return buf;
      if (hit === DONE_MARKER) sawDone = true;
      else sawNoVerify = true;
      buf = buf.slice(0, at) + buf.slice(at + hit.length);
    }
  };

  return {
    get sawMarker(): boolean {
      return sawDone;
    },
    get sawNoVerify(): boolean {
      return sawNoVerify;
    },
    push(delta: string): string {
      const buf = strip(tail + delta);
      const hold = markerPrefixSuffix(buf);
      tail = hold > 0 ? buf.slice(buf.length - hold) : '';
      return hold > 0 ? buf.slice(0, buf.length - hold) : buf;
    },
    flush(): string {
      const rest = tail;
      tail = '';
      return rest;
    },
  };
}
